import React from 'react';
import { Duration } from '@common/tokens';
import { Select as AntSelect } from 'antd';
import type { SelectProps } from 'antd';
import clsx from 'clsx';

/**
 * Thin wrapper over the Antd Select (per the antd wrapper standardization plan):
 * single import source through @common/components and a `wk-select` class hook.
 * Generic over the value type so `value`/`onChange` stay strongly typed.
 *
 * In `mode="multiple"`/`"tags"` antd hides the chevron by default (`showArrow`
 * defaults to `false`). We opt it back in so multi-selects match the regular
 * Select, and hide it on hover when the clear button takes its place — antd
 * renders both at the same position, so without this they'd overlap.
 */
export function Select<ValueType = unknown>({
  mode,
  className,
  showArrow,
  ...props
}: Omit<SelectProps<ValueType>, 'style'>) {
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const mergedShowArrow = showArrow ?? (isMultiple || undefined);

  return (
    <>
      <AntSelect<ValueType>
        {...props}
        mode={mode}
        style={{ width: '100%' }}
        showArrow={mergedShowArrow}
        className={clsx('wk-select', className)}
      />
      <style jsx>{`
        :global(.wk-select.ant-select-multiple .ant-select-arrow) {
          transition: opacity ${Duration.fast} ease;
        }
        /*
         * The clear button only renders when a value is present and reuses the
         * arrow's slot, so fade the chevron out on hover to let it through.
         */
        :global(
          .wk-select.ant-select-multiple.ant-select-allow-clear:hover:has(
              .ant-select-clear
            )
            .ant-select-arrow
        ) {
          opacity: 0;
        }
      `}</style>
    </>
  );
}

Select.Option = AntSelect.Option;
Select.OptGroup = AntSelect.OptGroup;
